import type { FolderAnalysisStatus } from '../shared/desktop-api';
import { setupPausedForCortexa } from '../paige/cortexa-wait';
import { hostWords } from '../shared/host-words';

/** Stop setup cancels the running native work; this is how that cancellation reads when it comes back. */
export const canceledByStop = (message: string): boolean =>
  /\b(?:a )?(?:task|operation) was cancel+ed\b/i.test(message);

/**
 * The person pressed Stop setup: main saves, and answers the running work with, "Setup stopped. Your completed work
 * is saved.", and refuses to pick it up again on its own ("Setup was stopped. Choose Try again…"). That is their
 * choice, never a failure (telemetry sends it as setup.stopped, not setup.failed).
 */
export const stoppedByPerson = (message: string | null | undefined): boolean =>
  /Setup stopped\. Your completed work is saved\.|Setup was stopped\. Choose Try again/.test(
    message ?? '',
  );

/**
 * Try again on a stopped setup that main did not take up again (it was preparing an update, say): the setup never ran,
 * so the banner says that instead of the words it showed before the press (October 7, 2026). The exact error stays
 * behind Technical details.
 */
export const setupResumeNotStartedWords =
  "Setup couldn't start again. Your completed work is still saved. Try again in a moment.";

export const engineStoppedSetupMessage =
  'The document engine stopped unexpectedly, and Paige has restarted it. Your place in this setup is saved: resume to continue where it stopped, without choosing the folder again.';

/**
 * What a setup that failed says, from the raw failure and the status main saved for it.
 * A saved place is described as one (resume it); a stop is the stop; anything else is the raw reason.
 */
export function setupFailureMessage(
  raw: string,
  status: Pick<FolderAnalysisStatus, 'state' | 'operationId' | 'failure'> | null,
): string {
  const resumable = status?.state === 'recoverable' && Boolean(status.operationId);
  // PAIGE (paige/cortexa-wait.ts): a setup paused because Cortexa took too long to start keeps its own words.
  if (raw === setupPausedForCortexa) return raw;
  if (
    resumable &&
    /document engine (?:stopped unexpectedly|is shutting down|was restarted)/i.test(raw)
  )
    return engineStoppedSetupMessage;
  if (resumable)
    return 'Setup stopped before it finished. Your place is saved: resume to continue where it stopped, without choosing the folder again.';
  if (canceledByStop(raw) && status?.failure) return status.failure;
  return raw;
}

/** Why a setup failed, as a code word (telemetry catalog `setup.failed`): never the engine's words. */
export type SetupFailureCategory = 'ocr' | 'memory' | 'engine' | 'unsupported' | 'other';

/** The same cases `setupFailureWords` tells apart, and the engine stopping and files it cannot read. */
export function setupFailureCategory(raw: string | null | undefined): SetupFailureCategory {
  const text = raw ?? '';
  if (/recogni[sz]er|\bOCR\b/i.test(text)) return 'ocr';
  if (/out of memory|not enough memory|insufficient memory/i.test(text)) return 'memory';
  if (
    /document engine (?:stopped unexpectedly|is shutting down|was restarted)|stopped reporting progress|engine (?:is not running|did not start)/i.test(
      text,
    )
  )
    return 'engine';
  if (
    /no supported|not a supported|unsupported|cannot be read as a (?:PDF|document|image)/i.test(
      text,
    )
  )
    return 'unsupported';
  return 'other';
}

/**
 * The plain words a failed setup's banner leads with; the engine's exact text stays one click
 * away under Technical details (owner rule: friendly words first, the exact error behind a
 * details control, never the raw text alone). Seen October 3, 2026: the banner showed only
 * "The local page recogniser failed 6 pages in a row… Worker exit code -1. Worker details:
 * capturepoint-ocr ready device=cpu recBatch=16…". Each line says only what is true of every
 * case it covers: setup never changes the chosen files, and the page-reader stop keeps
 * nothing from that try (its own message says so).
 */
export function setupFailureWords(raw: string | null | undefined): string {
  const text = raw ?? '';
  if (/recogni[sz]er|\bOCR\b/i.test(text))
    return 'Some pages could not be read, so setup stopped instead of guessing. Nothing from this try was kept, and your documents were not changed. Try again.';
  // PAIGE: a host that reads elsewhere says what is true there (host-words.ts).
  if (/out of memory|not enough memory|insufficient memory/i.test(text))
    return (
      hostWords().setupOutOfMemory ??
      'This computer ran out of memory, so setup stopped. Your documents were not changed. Close other programs, then try again.'
    );
  // Short enough to stay on one line beside its buttons (owner, 2026-10-19).
  return 'Setup stopped before it finished. Try again, or start over.';
}
